import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EyeIcon } from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@component/select";
import { Tabs, TabsContent } from "@component/tabs";
import { FormAssessment } from "@type/assessments";
import { createAssessment, getAssessmentDetail, updateAssessment } from "@service/assessment";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { formatHumanReadable } from "@util/date";
import JobLinkedAssessmentSection from "./job-linked";
import BreadcrumbNav from "@component/breadcrumb-nav";
import AssessmentTabsNavigation from "./assessment-tabs";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@component/dialog";
import Quiz from "@page/apply-now-quiz";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Tooltip } from "@component/tooltip";
import FormSettingsSection, { FormSettingsData } from "./form-settings-section";
import FormQuestionsSection from "./form-questions-section";

interface Props {
    isEditMode?: boolean;
}

export default function AssessmentForm(props: Props) {
    const { assessment_id } = useParams();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [, userInfo] = useUserProfileContext();
    const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "details");
    const [isLoadingAssessment, setIsLoadingAssessment] = useState<boolean>(false);
    const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
    const [isPreviewQuizOpen, setIsPreviewQuizOpen] = useState<boolean>(false);
    const [originalData, setOriginalData] = useState<FormAssessment>({
        type: "Form",
        status: "Active",
        content: {
            title: "",
            description: "",
            questions: [],
        },
    });
    const [formData, setFormData] = useState<FormAssessment>({
        type: "Form",
        status: "Active",
        content: {
            title: "",
            description: "",
            questions: [],
        },
    });

    // Form Settings state (synced with formData)
    const [formSettings, setFormSettings] = useState<FormSettingsData>({
        description: "",
        includeInCandidateScoring: false,
    });

    useEffect(() => {
        if (props.isEditMode && assessment_id) {
            setIsLoadingAssessment(true);
            getAssessmentDetail(assessment_id).then((resp) => {
                if (resp.status === 200) {
                    setFormData(resp.data.data);
                    setOriginalData(resp.data.data);
                    setFormSettings({
                        description: resp.data.data.content?.description || "",
                        includeInCandidateScoring: resp.data.data.content?.include_in_candidate_scoring || false,
                    });
                }
            }).catch((error) => {
                snackbar.showError("Failed to fetch screening details. Please try again.");
                catchErrorLog(`Failed to fetch assessment details for id ${assessment_id}, error: ${error}`);
            }).finally(() => {
                setIsLoadingAssessment(false);
            })
        }
    }, [props.isEditMode, assessment_id])

    const handleSave = () => {
        setIsLoadingSave(true);
        const payload = {
            ...formData,
            content: {
                ...formData.content,
                questions: formData.content.questions
            },
        };
        console.log("Payload to save:", payload);
        if (props.isEditMode) {
            updateAssessment(assessment_id as string, {
                ...payload
            }).then((resp) => {
                if (resp.success) {
                    snackbar.showSuccess("Screening updated successfully.");
                }
            }).catch((error) => {
                snackbar.showError("Failed to update screening. Please try again.");
                catchErrorLog(`Failed to update assessment for id ${assessment_id}, error: ${error}`);
            }).finally(() => {
                setIsLoadingSave(false);
            })
        } else {
            createAssessment(payload).then((resp) => {
                if (resp.success) {
                    snackbar.showSuccess("Screening created successfully.");
                    setTimeout(() => {
                        navigate("/assess");
                    }, 2000)
                }
            }).catch((error) => {
                snackbar.showError("Failed to create screening. Please try again.");
                catchErrorLog(`Failed to create assessment, error: ${error}`);
            }).finally(() => {
                setIsLoadingSave(false);
            });
        }
    };

    const handleFormSettingsChange = (data: FormSettingsData) => {
        setFormSettings(data);
        setFormData({
            ...formData,
            content: {
                ...formData.content,
                description: data.description,
                include_in_candidate_scoring: data.includeInCandidateScoring,
            },
        });
    };

    const handleQuestionsChange = (questions: FormAssessment["content"]["questions"]) => {
        setFormData({
            ...formData,
            content: {
                ...formData.content,
                questions,
            },
        });
    };

    const isChanges = formData.content.title !== originalData.content.title ||
        formData.content.description !== originalData.content.description ||
        JSON.stringify(formData.content.questions) !== JSON.stringify(originalData.content.questions);

    const isMember = userInfo?.permission === "member";
    const isDefault = formData.is_default === true;
    // Edit/create form assessment is blocked once the workspace is restricted
    // (trial expired + past grace) or the trial AI-assessment quota is reached.
    const { isAssessmentActionBlocked } = useSubscription();

    return (
        <>
            <div className="min-h-screen bg-white">
                <div
                    className="w-full p-6 space-y-8"
                    style={{
                        paddingTop: "0px",
                        paddingBottom: "0px",
                        paddingRight: "0px",
                        paddingLeft: "0px",
                    }}
                >
                    {/* Breadcrumb */}
                    <div className="flex items-center justify-between">
                        <BreadcrumbNav
                            items={[
                                { label: "Screen", href: "/assess" },
                                { label: props.isEditMode ? formData.content.title || "Untitled form" : "Create Form" },
                            ]}
                        />
                    </div>

                    {/* Tabs */}
                    <Tabs value={activeTab} onValueChange={(tab) => {
                        setActiveTab(tab);
                        setSearchParams({ tab });
                    }} className="w-full">
                        {props.isEditMode && (
                            <AssessmentTabsNavigation />
                        )}

                        {/* Title Row - Only in Details tab */}
                        {activeTab === "details" && (
                            <div className="px-0 py-0">
                                <div
                                    className="flex items-start justify-between"
                                    style={{ paddingBottom: "16px" }}
                                >
                                    {/* Left side - Title with Status and Timestamp */}
                                    <div className="flex flex-col gap-2">
                                        {/* Title */}
                                        <div className="relative">
                                            <Input
                                                value={formData.content.title}
                                                onChange={(e) => {
                                                    if (e.target.value.length <= 80 || e.target.value.length < (formData.content.title || "").length) {
                                                        setFormData({
                                                            ...formData,
                                                            content: { ...formData.content, title: e.target.value }
                                                        });
                                                    }
                                                }}
                                                maxLength={80}
                                                placeholder="Enter screening title..."
                                                className="text-2xl font-semibold border border-gray-300 rounded-md px-3 py-2 pr-32 h-auto focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 hover:border-gray-400 transition-colors min-w-[600px] w-full max-w-[900px] bg-white"
                                                disabled={isMember || isDefault || isAssessmentActionBlocked}
                                            />
                                            <span className="absolute right-3 bottom-2 text-xs text-gray-400 pointer-events-none">
                                                {(formData.content.title || "").length}/80 characters
                                            </span>
                                        </div>

                                        {/* Status and Timestamp row */}
                                        <div className="flex items-center gap-4 mb-4">
                                            <Select
                                                value={formData.status}
                                                disabled={isMember || isDefault || isAssessmentActionBlocked}
                                                onValueChange={(value) =>
                                                    setFormData({ ...formData, status: value })
                                                }
                                            >
                                                <SelectTrigger className="w-32">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className={`w-2 h-2 rounded-full ${formData.status === "Active"
                                                                ? "bg-green-500"
                                                                : formData.status === "Draft"
                                                                    ? "bg-yellow-500"
                                                                    : "bg-gray-400"
                                                                }`}
                                                        />
                                                        <span>{formData.status}</span>
                                                    </div>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Active">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-green-500" />
                                                            Active
                                                        </div>
                                                    </SelectItem>
                                                    <SelectItem value="Draft">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-yellow-500" />
                                                            Draft
                                                        </div>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>

                                            {/* Timestamp */}
                                            {props.isEditMode && (
                                                <span className="text-sm font-normal text-gray-600">
                                                    Last Updated {formatHumanReadable(formData?.updated_at || "", true)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    {!isMember && !isDefault && (
                                        <div className="flex items-center gap-3">
                                            <Tooltip content="Complete your form details to preview how it looks to candidates.">
                                                <Button
                                                    variant="outline"
                                                    className="flex items-center gap-2 font-medium"
                                                    disabled={formData.content.questions.length === 0 || !formData.content.title}
                                                    onClick={() => setIsPreviewQuizOpen(true)}
                                                >
                                                    <EyeIcon className="h-4 w-4" />
                                                    Preview Form
                                                </Button>
                                            </Tooltip>
                                            <Button
                                                onClick={handleSave}
                                                disabled={isLoadingSave || !isChanges || !formData.content.description?.trim() || formData.content.questions.length < 1 || isAssessmentActionBlocked}
                                                title={isAssessmentActionBlocked ? "Your trial has expired. Upgrade to edit screenings." : undefined}
                                                className="bg-blue-600 hover:bg-blue-700 font-medium"
                                            >
                                                {isLoadingSave ? "Saving..." : "Save Changes"}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Details Tab Content */}
                        <TabsContent
                            value="details"
                            className="space-y-12 py-8"
                            style={{ marginTop: "0px", paddingTop: "0px" }}
                        >
                            <div className="space-y-8">
                                {/* Form Settings Section */}
                                <div className="space-y-6">
                                    <FormSettingsSection
                                        data={formSettings}
                                        onDataChange={handleFormSettingsChange}
                                        disabled={isMember || isDefault || isAssessmentActionBlocked}
                                    />
                                </div>

                                {/* Form Questions Section */}
                                <div className="space-y-6">
                                    <FormQuestionsSection
                                        questions={formData.content.questions}
                                        onQuestionsChange={handleQuestionsChange}
                                        disabled={isMember || isDefault || isAssessmentActionBlocked}
                                        isLoadingAssessment={isLoadingAssessment}
                                        showPoints={formSettings.includeInCandidateScoring}
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        {/* Jobs Tab Content */}
                        <TabsContent value="jobs" className="space-y-6">
                            <JobLinkedAssessmentSection assessment_id={formData.id || ""} />
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            <Dialog open={isPreviewQuizOpen} onOpenChange={setIsPreviewQuizOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto py-5">
                    <DialogHeader>
                        <div className="flex items-start justify-between mx-5">
                            <div>
                                <DialogTitle className="text-xl font-semibold text-blue-600">
                                    Form Preview
                                </DialogTitle>
                                <DialogDescription className="text-sm text-muted-foreground mt-1">
                                    Preview how your form will appear to candidates.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    <div className="mx-5">
                        <Quiz
                            jobId=""
                            isPreviewMode={true}
                            surveyCompleted={false}
                            assessment={formData}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
